import React, { useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import CardDrag from './CardDrag';
import Loader from '../../../../../Components/Utility/Loader/Loader';
import * as action from '../../../store/DevelopmentOrder.action';
import ConfirmationPopup from '../../../../../Components/ModalPopup/ConfirmationPopup';
import ImageUploadModal from '../../../../../Components/ModalPopup/ImageUploadModal';
import { getImagesByLocation, IMAGE_PICKER_EXTENSIONS } from '../../../../AssetLibrary/Assets/store/asset.service';
import SampleAnnotation from '../../../SampleAnnotation/SampleAnnotation';
import DragDropSort from '../../../../../Components/DragDropSort/DragDropSort';


/**
 * Reads a picked file into a base64 data URL, the shape the annotation API uploads to S3.
 * @param {File} imageFile - File chosen in the upload modal
 * @returns {Promise<string>} Base64 data URL
 */
const readFileAsDataUrl = (imageFile) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(imageFile);
  });

/**
 * Builds the annotation upload payload from what the upload modal returned. An asset picked from
 * the library travels as its S3 object key plus its assetId, so the API stores that key and records
 * the asset usage without copying anything; a picked file travels as a base64 data URL for the API
 * to upload, carrying `isAddToAssetLibrary` when the user asked for it to be filed in the library.
 * @param {Object} uploadData - { file, assetKey, assetId, addToAssetLibrary } from ImageUploadModal
 * @returns {Promise<Object|null>} Request body for setSampleAnnotations, or null when nothing was picked
 */
const buildAnnotationImagePayload = async ({ file, assetKey, assetId, addToAssetLibrary }) => {
  if (assetKey) {
    return { annotaeImage: assetKey, assetId: assetId || null };
  }
  if (!file) {
    return null;
  }
  return {
    annotaeImage: await readFileAsDataUrl(file),
    assetId: null,
    isAddToAssetLibrary: addToAssetLibrary === true,
  };
};

const Annotations = forwardRef(({ show, toggle, SampleDeleteAccess, SampleAddEditAccess }, ref) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const valueRef = useRef();
  const originalDataRef = useRef([]);
  const devOrderReducer = useSelector((reducerstate) => reducerstate);
  const {
    Measuremets,
    UserDetails,
    BOMConfigDetails: { isProductArchived },
  } = devOrderReducer;

  const { sampleAnnotationImage } = Measuremets;
  const { isSupplierLoggedIn } = UserDetails;

  const queryParams = useLocation();
  const { search } = queryParams;
  const smapleId = new URLSearchParams(search).get('sample');

  const history = useHistory();
  const { devOrderId } = useParams();
  const [sampleAnnotationData, setSampleAnnotationData] = useState([]);

  const [loader, setLoader] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [deleteImageId, setDeleteImageId] = useState({});
  const [selectedAnnotationImg, setSelectedAnnotationImg] = useState({
    show: false,
    img: null,
    sampleId: null,
  });
  const [showDragDrop, setShowDragDrop] = useState(false);
  const [itemList, setitemList] = useState([]);

  const reArrangeImage = async (e) => {
    e.preventDefault();
    const itemslistData = sampleAnnotationData;
    await setitemList(itemslistData);
    setShowDragDrop(!showDragDrop);
  };
  useImperativeHandle(ref, () => ({
    getSampleAnnotation() {
      return sampleAnnotationData;
    },
    async reArrangeImage(e) {
      await reArrangeImage(e);
    },
    hasPendingChanges() {
      if (sampleAnnotationData.length !== originalDataRef.current.length) {
        return false;
      }

      const hasChanges = sampleAnnotationData.some((item, index) => {
        const originalItem = originalDataRef.current[index];
        return item?.name !== originalItem?.name;
      });
      console.log('hasChanges', hasChanges);
      return hasChanges;
    },
  }));

  const fetchSampleImages = useCallback(
    (searchText, page, limit, allowedImageExtensions) => getImagesByLocation('Samples', devOrderId, searchText, allowedImageExtensions, page, limit),
    [devOrderId],
  );

  /**
   * Saves the image chosen in the upload modal and refreshes the annotation list.
   * @param {Object} uploadData - { file, assetKey, assetId } from ImageUploadModal
   */
  const handleImageUpload = async (uploadData) => {
    const imageData = await buildAnnotationImagePayload(uploadData || {});
    if (!imageData?.annotaeImage?.length) {
      return;
    }

    const params = {
      devOrderId,
      sampleId: smapleId,
    };

    await dispatch(action.setSampleAnnotations(params, imageData, t));
    getSampleAnnotationDetail();
  };

  const annotatedImage = (obj) => {
    const { img, id } = obj;
    const path = `/development-order/${devOrderId}/sample/${smapleId}?imgName=${id}`;
    history.push(path);
    setSelectedAnnotationImg({ show: true, img, sampleId: id });
  };

  const deleteImg = async () => {
    await dispatch(action.deleteSampleAnnotations(devOrderId, smapleId, deleteImageId, t));
    setDeleteImageId({});
    setShowConfirmation(false);
    getSampleAnnotationDetail();
  };

  useEffect(() => {
    getSampleAnnotationDetail();
  }, []);

  useEffect(() => {
    if (sampleAnnotationImage?.sampleAnnotateImages) {
      setSampleAnnotationData(sampleAnnotationImage?.sampleAnnotateImages);
      originalDataRef.current = cloneDeep(sampleAnnotationImage?.sampleAnnotateImages);
    }
  }, [sampleAnnotationImage]);

  const handleNameChange = (e, i) => {
    const copydata = [...sampleAnnotationData];
    copydata[i].name = e.target.value;
    setSampleAnnotationData(copydata);
  };

  const getSampleAnnotationDetail = async () => {
    setLoader(true);
    const params = {
      devOrderId,
      sampleId: smapleId,
    };
    await dispatch(action.getSampleAnnotations(params));
    setLoader(false);
  };

  const renderCard = (card, index) => {
    return (
      <CardDrag
        key={card?.id}
        index={index}
        id={card?.id}
        item={card}
        handleNameChange={handleNameChange}
        setDeleteImageId={setDeleteImageId}
        setShowConfirmation={setShowConfirmation}
        annotatedImage={annotatedImage}
        isSupplierLoggedIn={isSupplierLoggedIn}
        isProductArchived={isProductArchived}
        SampleDeleteAccess={SampleDeleteAccess}
        SampleAddEditAccess={SampleAddEditAccess}
      />
    );
  };

  useEffect(() => {
    valueRef.current = sampleAnnotationData;
  }, [sampleAnnotationData]);

  const hideShowDragDrop = () => {
    setShowDragDrop(!showDragDrop);
  };
  const handleSaveSequence = async (_e, sequenceList) => {
    const sampleId = sequenceList?.sequence[0].sampleId;

    const params = {
      sampleId,
      sampleAnnotateImages: sequenceList?.sequence,
    };

    await action.saveSampleAnnoationsSequence(params, t);
    getSampleAnnotationDetail();
    hideShowDragDrop();
  };
  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <div className="pt-4">
          {sampleAnnotationData.length > 0 ? (
            sampleAnnotationData?.map((sTEle, sTIndex) => renderCard(sTEle, sTIndex))
          ) : (
            <p className="text-center">{t('SAMPLES.SAMPLE_ANNOTATION.NO_IMAGES_FOUND')}</p>
          )}
        </div>
      )}

      {show && (
        <ImageUploadModal
          show={show}
          fetchImages={fetchSampleImages}
          allowedImageExtensions={IMAGE_PICKER_EXTENSIONS}
          showAddToAssetLibrary
          onClose={toggle}
          onSave={handleImageUpload}
        />
      )}

      {showConfirmation && (
        <ConfirmationPopup
          onOpen={showConfirmation}
          onConfirm={() => deleteImg()}
          title={t('SAMPLES.SAMPLE_ANNOTATION.DELETE_SAMPLE_IMAGE')}
          message={t('SAMPLES.SAMPLE_ANNOTATION.DELETE_SELECTED_MESSAGE')}
          toggle={() => {
            setShowConfirmation(false);
          }}
        />
      )}

      {selectedAnnotationImg.show && <SampleAnnotation imgPath={selectedAnnotationImg.img} match={{ devOrderId, sampleId: selectedAnnotationImg?.sampleId }} />}
      {showDragDrop && (
        <DragDropSort
          devOrderId={devOrderId}
          itemList={itemList?.map((ele, i) => {
            ele._id = i;
            ele.sampleId = sampleAnnotationImage?.sampleId;
            return ele;
          })}
          isShow={showDragDrop}
          isToggle={hideShowDragDrop}
          saveSequence={handleSaveSequence}
          fromComp="sampleAnnotations"
        />
      )}
    </>
  );
});

Annotations.displayName = 'Annotations';

export default Annotations;
